import { useTranslation } from "react-i18next";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { applicationMethods, ApplicationMethod } from "@/data/jobOptions";
import { JobFormData } from "@/pages/dashboard/JobCreate";
import { Mail, ExternalLink, Upload, Check } from "lucide-react";

interface JobFormStep4Props {
  formData: JobFormData;
  updateFormData: (updates: Partial<JobFormData>) => void;
}

export function JobFormStep4({ formData, updateFormData }: JobFormStep4Props) {
  const { t } = useTranslation();

  const getIcon = (method: ApplicationMethod) => {
    switch (method) {
      case 'email': return <Mail className="w-5 h-5" />;
      case 'external_link': return <ExternalLink className="w-5 h-5" />;
      case 'upload_cv': return <Upload className="w-5 h-5" />;
      default: return <Mail className="w-5 h-5" />;
    }
  };

  const getDescription = (method: ApplicationMethod) => {
    switch (method) {
      case 'email': return t("jobs.form.emailMethodDesc");
      case 'external_link': return t("jobs.form.externalMethodDesc");
      case 'upload_cv': return t("jobs.form.uploadMethodDesc");
      default: return "";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-xl font-bold text-dark mb-2">
          {t("jobs.form.step4Title")}
        </h2>
        <p className="text-muted-foreground">
          {t("jobs.form.step4Desc")}
        </p>
      </div>

      {/* Application Method */}
      <div>
        <label className="block font-semibold text-dark mb-4">
          {t("jobs.form.applicationMethod")} *
        </label>
        
        <div className="space-y-3">
          {applicationMethods.map((method) => {
            const isSelected = formData.application_method === method.value;
            return (
              <button
                key={method.value}
                type="button"
                onClick={() => updateFormData({ application_method: method.value })}
                className={`w-full p-4 border-[3px] border-dark text-left transition-all flex items-center gap-4 ${
                  isSelected 
                    ? 'bg-primary shadow-[4px_4px_0px_#101322] -translate-x-1 -translate-y-1' 
                    : 'bg-card hover:bg-surface'
                }`}
              >
                <div className={`p-2 border-[2px] border-dark ${isSelected ? 'bg-card' : 'bg-surface'}`}>
                  {getIcon(method.value)}
                </div>
                <div className="flex-1">
                  <div className="font-heading font-bold text-dark">{t(method.labelKey)}</div>
                  <div className="text-sm text-muted-foreground">{getDescription(method.value)}</div>
                </div>
                {isSelected && <Check className="w-5 h-5 text-dark" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Email input */}
      {formData.application_method === 'email' && (
        <div>
          <label className="block font-semibold text-dark mb-2">
            {t("jobs.form.applicationEmail")} *
          </label>
          <BrutalInput
            type="email"
            value={formData.application_email}
            onChange={(e) => updateFormData({ application_email: e.target.value })}
            placeholder={t("jobs.form.emailPlaceholder")}
          />
          <p className="text-xs text-muted-foreground mt-2">
            {t("jobs.form.emailHint")}
          </p>
        </div>
      )}

      {/* External URL input */}
      {formData.application_method === 'external_link' && (
        <div>
          <label className="block font-semibold text-dark mb-2">
            {t("jobs.form.applicationUrl")} *
          </label>
          <BrutalInput
            type="url"
            value={formData.application_url}
            onChange={(e) => updateFormData({ application_url: e.target.value })}
            placeholder={t("jobs.form.urlPlaceholder")}
          />
          <p className="text-xs text-muted-foreground mt-2">
            {t("jobs.form.urlHint")}
          </p>
        </div>
      )}

      {/* Platform info */}
      {formData.application_method === 'upload_cv' && (
        <div className="p-4 bg-primary/10 border-[2px] border-primary">
          <p className="text-sm text-dark">
            {t("jobs.form.platformInfo")}
          </p>
        </div>
      )}
    </div>
  );
}
